/**
 * Offers a reload when a newer build has been deployed under an open session.
 *
 * Deliberately a prompt and not an automatic reload: couriers fill long
 * onboarding forms, and reloading under them would discard whatever is not yet
 * autosaved. Dismissible, and it comes back on the next poll.
 */

import React, { useState } from 'react';
import { Button, Snackbar } from '@mui/material';
import { RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useAppVersionCheck } from 'shared/hooks/useAppVersionCheck';

const NewVersionPrompt: React.FC = () => {
  const { t } = useTranslation();
  const updateAvailable = useAppVersionCheck();
  const [dismissed, setDismissed] = useState(false);

  return (
    <Snackbar
      open={updateAvailable && !dismissed}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      onClose={(_event, reason) => {
        // A stale client is worth more than one glance, so ignore stray clicks.
        if (reason !== 'clickaway') setDismissed(true);
      }}
      message={t('common.newVersion.message') as string}
      sx={{ zIndex: 99998 }}
      action={
        <Button
          size="small"
          color="primary"
          variant="contained"
          startIcon={<RefreshCw size={16} />}
          onClick={() => window.location.reload()}
        >
          {t('common.newVersion.action') as string}
        </Button>
      }
    />
  );
};

export default NewVersionPrompt;
